import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const FAQ_ITEMS = [
  {
    question: "What are browser games?",
    answer:
      "Games you play inside your web browser (Chrome, Safari, Firefox, Edge, etc.) without downloading or installing anything. OctoGames hosts HTML5 and similar web games that work on most devices."
  },
  {
    question: "Are OctoGames free?",
    answer:
      "Yes. All games are free to play. You can play as a guest or create an account to save favorites and track your progress."
  },
  {
    question: "Can I play on mobile?",
    answer:
      "Yes. The site works on phones and tablets; many games are touch-friendly. You can also use our Android APK for an app-style shortcut."
  }
];

/**
 * SEO text block with expand/collapse. Renders between games grid and footer.
 * Includes internal links to games and genre pages. Injects FAQ schema for rich results.
 */
const SeoTextBlock = ({ featuredGames = [], genreLinks = [], currentGenre }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer }
      }))
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-faq", "true");
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.head.querySelector('script[data-seo-faq="true"]');
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="up-seo-block container" aria-label="About OctoGames and browser games">
      <div className={"up-seo-block-inner" + (expanded ? " up-seo-block-expanded" : "")}>
        <h2 className="up-seo-block-title">About OctoGames</h2>

        <p className="up-seo-block-p">
          <strong>OctoGames</strong> is a free online games platform where you can play hundreds of
          browser games instantly—no download, no install, no sign-up required. Every title runs
          directly in your browser on desktop, tablet, or phone. We host HTML5 and modern web games
          for a smooth experience with no plugins.
        </p>

        <p className="up-seo-block-p">
          Browse by genre, sort by popularity or date added, and search by name. Create a free
          account to save favorites and track what you&apos;ve played. New games are added regularly;
          check the <Link to="/news">News Hub</Link> for updates.
        </p>

        {genreLinks.length > 0 && (
          <>
            <h3 className="up-seo-block-h3">Explore by genre</h3>
            <p className="up-seo-block-p">
              Find something that matches your mood. Our most popular categories include:
            </p>
            <ul className="up-seo-block-list">
              {genreLinks.slice(0, 8).map((g) => (
                <li key={g.slug}>
                  <Link to={`/genre/${g.slug}`}>{g.name}</Link>
                </li>
              ))}
            </ul>
            <p className="up-seo-block-p">
              Use the genre filters above or open any category page to see all games in that genre.
            </p>
          </>
        )}

        {featuredGames.length > 0 && (
          <>
            <h3 className="up-seo-block-h3">
              {currentGenre ? `Popular ${currentGenre} games` : "Popular games to play now"}
            </h3>
            <p className="up-seo-block-p">
              Try these free browser games—click to play. No account needed:
            </p>
            <ul className="up-seo-block-list up-seo-block-games">
              {featuredGames.slice(0, 10).map((g) => (
                <li key={g.slug}>
                  <Link to={`/game/${g.slug}`}>{g.title}</Link>
                </li>
              ))}
            </ul>
            <p className="up-seo-block-p">
              <Link to="/">Browse the full collection</Link> or use the search bar to find a
              specific game.
            </p>
          </>
        )}

        <h3 className="up-seo-block-h3">No download, no install</h3>
        <p className="up-seo-block-p">
          Everything runs in the browser. There is nothing to install on your computer or phone.
          For Android users we offer an <Link to="/download">APK download</Link> if you prefer an
          app icon—you get the same web-based games. All games are free; the site is supported by
          ads so you can keep playing without paying.
        </p>

        <h3 className="up-seo-block-h3">FAQ</h3>
        <dl className="up-seo-block-faq">
          {FAQ_ITEMS.map((item) => (
            <React.Fragment key={item.question}>
              <dt>{item.question}</dt>
              <dd>
                {item.question === "Can I play on mobile?" ? (
                  <>
                    Yes. The site works on phones and tablets; many games are touch-friendly. You can
                    also use our <Link to="/download">Android APK</Link> for an app-style shortcut.
                  </>
                ) : (
                  item.answer
                )}
              </dd>
            </React.Fragment>
          ))}
        </dl>

        <p className="up-seo-block-p up-seo-block-cta">
          Play the best free online games at OctoGames—no download, no install. Browse by genre,
          try popular titles, and discover something new.
        </p>
      </div>

      <button
        type="button"
        className="up-seo-block-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </section>
  );
};

export default SeoTextBlock;
